import type { UserProfilePatchInput } from '@today/models'
import type { UserProfileDto } from '@today/types'
import { useState } from 'react'
import { apiMessage } from '@/hooks/offers/api-message'
import { useProfilePatch } from '@/hooks/useUserProfile'

/** E.164, as the API checks it (TO-197 REQ-004). */
const E164 = /^\+[1-9][0-9]{1,14}$/
/** A plausible address; the API's check is authoritative. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type ContactPatch = NonNullable<UserProfilePatchInput['personalContact']>

/** The changed personal-contact keys of a draft ('' = no override), or undefined when none changed. */
const contactChanges = (profile: UserProfileDto, phone: string, email: string): ContactPatch | undefined => {
	const next = { phone: phone.trim() || null, email: email.trim() || null }
	const changes: ContactPatch = {
		...(next.phone !== profile.personalContact.phone && { phone: next.phone }),
		...(next.email !== profile.personalContact.email && { email: next.email }),
	}
	return Object.keys(changes).length > 0 ? changes : undefined
}

/** Which draft overrides break their rule ('' is always allowed: no override). */
const refusalsOf = (phone: string, email: string) => ({
	phone: phone.trim() !== '' && !E164.test(phone.trim()),
	email: email.trim() !== '' && !EMAIL.test(email.trim()),
})

/** Edits of the "Contact on offers" section: the toggle and the overrides, validated before the save. */
export function useOfferContactForm(profile: UserProfileDto | undefined) {
	const [enabled, setEnabled] = useState<boolean | null>(null)
	const [phone, setPhone] = useState<string | null>(null)
	const [email, setEmail] = useState<string | null>(null)
	const [invalid, setInvalid] = useState({ phone: false, email: false })
	const patch = useProfilePatch()
	const draft = {
		enabled: enabled ?? profile?.showPersonalContactOnOffers ?? false,
		phone: phone ?? profile?.personalContact.phone ?? '',
		email: email ?? profile?.personalContact.email ?? '',
	}
	return {
		draft,
		invalid,
		error: patch.error ? (apiMessage(patch.error) ?? null) : null,
		hasFailed: patch.isError,
		isSaving: patch.isPending,
		saved: patch.isSuccess,
		setEnabled,
		setPhone: (value: string) => {
			setPhone(value)
			setInvalid((current) => ({ ...current, phone: false }))
		},
		setEmail: (value: string) => {
			setEmail(value)
			setInvalid((current) => ({ ...current, email: false }))
		},
		save: () => {
			if (!profile) return
			const refused = refusalsOf(draft.phone, draft.email)
			setInvalid(refused)
			if (refused.phone || refused.email) return
			const personalContact = contactChanges(profile, draft.phone, draft.email)
			const body: UserProfilePatchInput = {
				...(draft.enabled !== profile.showPersonalContactOnOffers && { showPersonalContactOnOffers: draft.enabled }),
				...(personalContact && { personalContact }),
			}
			if (Object.keys(body).length === 0) return
			patch.mutate(body, {
				onSuccess: () => {
					setEnabled(null)
					setPhone(null)
					setEmail(null)
				},
			})
		},
	}
}
